import React, { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { Milestone, MilestoneMessage } from '../types';
import ConflictFlowIllustration from './ConflictFlowIllustration';
import CaseThread, { formatCaseDate, isOwnMessage, isSystemEvent, type CaseComposer } from './CaseThread';

export type ObjectionStage = 'disputed' | 'under_resolution' | 'conflict';

/** The ÄTA linked to the objection, when one exists (ClickUp 869fh5n6z). */
export type ObjectionAta = {
  number: number;
  /** 'draft' = not yet sent by the company; 'signing' = awaits the buyer. */
  state: 'draft' | 'signing';
  reason?: string | null;
};

export type ObjectionAction = {
  label: string;
  onClick: () => void;
  disabled?: boolean;
  testId?: string;
};

export type ObjectionNextStep = {
  kind: 'action' | 'wait';
  title: string;
  text?: string;
  primary?: ObjectionAction & { tone?: 'cta' | 'navy' };
  secondary?: ObjectionAction;
  link?: ObjectionAction & { prompt?: string };
};

interface ObjectionCaseProps {
  milestone: Milestone;
  stage: ObjectionStage;
  /** The buyer only meets ÄTA wording once an ÄTA exists (ClickUp 869fgj7ck). */
  hasAta?: boolean;
  userRole: 'company' | 'person';
  messages: MilestoneMessage[];
  nextStep: ObjectionNextStep;
  /** Alternative path below the card, e.g. "Vill du hellre åtgärda arbetet?". */
  alternative?: { title: string; text: string; action: ObjectionAction };
  /** Notices that keep their own flows (buyer proposals, funding of a signed ÄTA). */
  notices?: React.ReactNode;
  escalation?: React.ReactNode;
  escalationOpen?: boolean;
  onReferenceMessage?: (message: MilestoneMessage) => void;
  referenceBusy?: boolean;
  composer: CaseComposer;
  error?: string | null;
}

const isOwn = isOwnMessage;
const formatDate = formatCaseDate;

const formatAmount = (amount: number) => `${new Intl.NumberFormat('sv-SE').format(amount)} kr`;

function ActionButton({ action, className }: { action: ObjectionAction; className: string }) {
  return (
    <button type="button" className={`btn ${className}`} onClick={action.onClick} disabled={action.disabled}
      data-testid={action.testId}>
      {action.label}
    </button>
  );
}

/** Primary button class: CTA green for the buyer's decisions, navy for the company. */
export const primaryClass = (tone: 'cta' | 'navy' = 'cta') => (tone === 'navy' ? 'btn-primary' : 'btn-success');

export type CaseStepState = 'done' | 'current' | 'pending' | 'warning' | 'error' | 'closed';

export type CaseStep = {
  label: string;
  hint?: string;
  /** Overrides the position-based look, e.g. a stage's attestation under objection. */
  state?: CaseStepState;
  /** Data attributes for the step, e.g. the timeline's `data-flow-substep`. */
  attributes?: Record<`data-${string}`, string | undefined>;
};

const STEP_STATE_CLASS: Record<CaseStepState, string> = {
  done: 'is-done',
  current: 'is-current',
  pending: '',
  warning: 'is-current is-warning',
  error: 'is-current is-error',
  closed: 'is-closed'
};

/** Where the case stands, as bars (one per step); a sentence on the phone. */
export function CaseSteps({ steps, currentStep }: { steps: CaseStep[]; currentStep: number }) {
  const { t } = useTranslation();
  const compactIndex = Math.min(currentStep, steps.length - 1);
  return (
    <>
      <ol className="objection-steps" aria-label={t('objectionCase.steps.label')}>
        {steps.map((step, index) => (
          <li key={step.label} {...step.attributes}
            className={`objection-step ${step.state ? STEP_STATE_CLASS[step.state]
              : index < currentStep ? 'is-done' : index === currentStep ? 'is-current' : ''}`}
            aria-current={index === currentStep ? 'step' : undefined}>
            <span className="objection-step-bar" aria-hidden="true"></span>
            <span className="objection-step-label">{step.label}</span>
            {step.hint && <span className="objection-step-hint">{step.hint}</span>}
          </li>
        ))}
      </ol>
      <p className="objection-steps-compact d-sm-none mb-0">
        {t('objectionCase.steps.compact', { current: compactIndex + 1, total: steps.length, label: steps[compactIndex].label })}
      </p>
    </>
  );
}

/** The one thing the viewer should do now, or what they are waiting for. */
export function NextStepCard({ nextStep, kicker, children }: { nextStep: ObjectionNextStep; kicker?: string; children?: React.ReactNode }) {
  const { t } = useTranslation();
  return (
    <section className={`objection-next ${nextStep.kind === 'wait' ? 'is-wait' : 'is-action'}`} aria-labelledby="objection-next-title"
      data-testid="objection-next-step" data-kind={nextStep.kind}>
      <span className="objection-next-icon" aria-hidden="true">
        <i className={nextStep.kind === 'wait' ? 'feather-clock' : 'feather-arrow-right'}></i>
      </span>
      <div className="objection-next-body">
        <div className="objection-kicker">
          {kicker ?? t(nextStep.kind === 'wait' ? 'objectionCase.kickerWait' : 'objectionCase.kickerAction')}
        </div>
        <h3 id="objection-next-title" className="objection-next-title">{nextStep.title}</h3>
        {nextStep.text && <p className="mb-0">{nextStep.text}</p>}
        {(nextStep.primary || nextStep.secondary) && (
          <div className="objection-next-actions">
            {nextStep.primary && (
              <ActionButton action={nextStep.primary} className={`${primaryClass(nextStep.primary.tone)} d-none d-sm-inline-flex`} />
            )}
            {nextStep.secondary && <ActionButton action={nextStep.secondary} className="btn-outline-primary" />}
          </div>
        )}
        {nextStep.link && (
          <div className="objection-next-link">
            {nextStep.link.prompt && <span>{nextStep.link.prompt} </span>}
            <ActionButton action={nextStep.link} className="btn-link objection-inline-link" />
          </div>
        )}
        {children}
      </div>
    </section>
  );
}

/**
 * The objection case in the attestation modal: where it stands, what the
 * viewer should do now, the objection and the money, the dialogue as a
 * thread and escalation folded away (ClickUp 869fh5n6z).
 */
export default function ObjectionCase({
  milestone,
  stage,
  hasAta = false,
  userRole,
  messages,
  nextStep,
  alternative,
  notices,
  escalation,
  escalationOpen = false,
  onReferenceMessage,
  referenceBusy = false,
  composer,
  error
}: ObjectionCaseProps) {
  const { t } = useTranslation();
  const [showHowItWorks, setShowHowItWorks] = useState(false);
  const escalationRef = useRef<HTMLDetailsElement>(null);
  const counterpartName = [...messages].reverse().find(message => !isSystemEvent(message) && !isOwn(message, userRole))?.userName;
  // Every objection stage is about finding a solution (step 2 of 4).
  const currentStep = 1;
  const now = t('objectionCase.steps.now');
  const steps = [
    { label: t(userRole === 'person' ? 'objectionCase.steps.objectedPerson' : 'objectionCase.steps.objectedCompany'), hint: formatDate(milestone.disputedAt) },
    { label: t('objectionCase.steps.solution'), hint: now },
    { label: t('objectionCase.steps.agreed'), hint: t(userRole === 'person' && !hasAta ? 'objectionCase.steps.agreedHintPerson' : 'objectionCase.steps.agreedHint') },
    { label: t('objectionCase.steps.payout'), hint: t(userRole === 'person' ? 'objectionCase.steps.payoutHintPerson' : 'objectionCase.steps.payoutHintCompany') }
  ];

  // Opening "Kommer ni inte överens?" brings its content into view.
  const onEscalationToggle = () => {
    const details = escalationRef.current;
    if (details?.open) window.setTimeout(() => details.scrollIntoView?.({ block: 'start', behavior: 'smooth' }), 0);
  };

  const objectionLabel = userRole === 'person'
    ? t('objectionCase.objection.personLabel', { date: formatDate(milestone.disputedAt, true) })
    : counterpartName
      ? t('objectionCase.objection.companyLabel', { name: counterpartName, date: formatDate(milestone.disputedAt, true) })
      : t('objectionCase.objection.companyLabelNoName', { date: formatDate(milestone.disputedAt, true) });

  return (
    <div className="objection-case" data-testid="objection-case" data-stage={stage}>
      {/* 2. Where the case stands. */}
      <div className="objection-steps-block">
        <CaseSteps steps={steps} currentStep={currentStep} />
        <button type="button" className="btn btn-link objection-how-link" aria-expanded={showHowItWorks}
          onClick={() => setShowHowItWorks(open => !open)}>
          {t(showHowItWorks ? 'objectionCase.hideHowItWorks' : 'objectionCase.howItWorks')}
        </button>
        {showHowItWorks && (
          <ConflictFlowIllustration currentStep={stage === 'conflict' ? 'locked' : 'dialogue'} viewerSide={userRole} />
        )}
      </div>

      {/* For the company the objection comes first: it is what to answer. */}
      {userRole === 'company' && milestone.disputeReason && (
        <div className="objection-quote is-company" data-testid="objection-reason">
          <div className="objection-quote-label">{objectionLabel}</div>
          <blockquote className="mb-0">”{milestone.disputeReason}”</blockquote>
        </div>
      )}

      {/* 3. One next step. */}
      <NextStepCard nextStep={nextStep} />

      {notices}

      {alternative && (
        <div className="objection-alternative">
          <div className="objection-alternative-text">
            <div className="fw-semibold">{alternative.title}</div>
            <div className="small">{alternative.text}</div>
          </div>
          <ActionButton action={alternative.action} className="btn-outline-primary" />
        </div>
      )}

      {/* 4. The objection and the money. */}
      <div className={`objection-facts ${userRole === 'person' && milestone.disputeReason ? 'has-quote' : ''}`}>
        {userRole === 'person' && milestone.disputeReason && (
          <div className="objection-quote" data-testid="objection-reason">
            <div className="objection-quote-label">{objectionLabel}</div>
            <blockquote className="mb-0">”{milestone.disputeReason}”</blockquote>
          </div>
        )}
        <div className="objection-money">
          <i className="feather-lock" aria-hidden="true"></i>
          <div>
            <div className="fw-semibold">{t('objectionCase.money.title', { amount: formatAmount(milestone.amount) })}</div>
            <div className="small">{t(userRole === 'person' ? 'objectionCase.money.person' : 'objectionCase.money.company')}</div>
          </div>
        </div>
      </div>

      {/* 5. The dialogue, in the same design as Meddelanden. */}
      <CaseThread messages={messages} userRole={userRole} composer={composer}
        onReferenceMessage={onReferenceMessage} referenceBusy={referenceBusy} error={error} />

      {/* 6. Escalation, folded away but easy to find. */}
      {escalation && (
        <details ref={escalationRef} className="objection-escalation" open={escalationOpen} onToggle={onEscalationToggle}
          data-testid="objection-escalation">
          <summary>
            <i className="feather-life-buoy objection-escalation-icon" aria-hidden="true"></i>
            <span className="objection-escalation-text">
              <span className="objection-escalation-title">
                {t(userRole === 'person' ? 'objectionCase.escalation.person' : 'objectionCase.escalation.company')}
              </span>
              <span className="objection-escalation-hint">
                {t(userRole === 'person' ? 'objectionCase.escalation.personHint' : 'objectionCase.escalation.companyHint')}
              </span>
            </span>
            <i className="feather-chevron-down objection-escalation-chevron" aria-hidden="true"></i>
          </summary>
          <div className="objection-escalation-body">{escalation}</div>
        </details>
      )}
    </div>
  );
}
